import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { Cm360Icon } from '../../assets/icons/BrandIcons';
import api from '../../config/api';

const GOOGLE_GREEN = '#34A853';

// Shown after the Campaign Manager 360 Google sign-in: one entry per CM360 account the login has a user profile in.
// Each account the user ticks becomes its own connector.
const Cm360Selection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [tempId, setTempId] = useState(null);
  const [selected, setSelected] = useState([]);           // one account at a time
  const [advertisers, setAdvertisers] = useState([]);
  const [pickedAdvertisers, setPickedAdvertisers] = useState([]);
  const [wholeAccount, setWholeAccount] = useState(false);  // explicit opt-in, never inferred from ticking every advertiser
  const [loadingAdvertisers, setLoadingAdvertisers] = useState(false);

  useEffect(() => {
    const temp_id = searchParams.get('temp_id');
    if (!temp_id) {
      setError('Invalid session. Please try authenticating again.');
      setLoading(false);
      return;
    }
    setTempId(temp_id);
    loadAccounts(temp_id);
  }, [searchParams]);

  const loadAccounts = async (temp_id) => {
    try {
      setLoading(true);
      const response = await api.get('/api/connectors/cm360/accounts', { params: { temp_id } });
      if (response.data.success) {
        setAccounts(response.data.accounts || []);
        setUserEmail(response.data.user_email || '');
      } else {
        setError(response.data.message || 'Failed to load Campaign Manager 360 accounts');
      }
    } catch (err) {
      console.error('Error loading Campaign Manager 360 accounts:', err);
      setError(err.response?.data?.message || 'Failed to load Campaign Manager 360 accounts');
    } finally {
      setLoading(false);
    }
  };

  // An account can hold several brands' advertisers (agencies): the brand picks the advertisers that are theirs.
  const toggle = async (id) => {
    if (connecting) return;
    if (selected.includes(id)) {
      setSelected([]);
      setAdvertisers([]);
      setPickedAdvertisers([]);
      setWholeAccount(false);
      return;
    }
    setSelected([id]);
    setAdvertisers([]);
    setPickedAdvertisers([]);
    setWholeAccount(false);
    setLoadingAdvertisers(true);
    try {
      const response = await api.get('/api/connectors/cm360/advertisers', { params: { temp_id: tempId, account_id: id } });
      setAdvertisers(response.data.advertisers || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load the account advertisers');
    } finally {
      setLoadingAdvertisers(false);
    }
  };

  const toggleAdvertiser = (id) => {
    if (connecting) return;
    setPickedAdvertisers(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const allPicked = advertisers.length > 0 && pickedAdvertisers.length === advertisers.length;

  const handleConnect = async () => {
    if (selected.length === 0 || pickedAdvertisers.length === 0) {
      setError('Please choose an account and the advertisers in it that belong to this brand');
      return;
    }
    setConnecting(true);
    setError('');
    try {
      const response = await api.post('/api/connectors/cm360/select', {
        temp_id: tempId,
        account_ids: selected,
        advertiser_ids: pickedAdvertisers,
        all_advertisers: wholeAccount
      });
      if (response.data.success) {
        try {
          await api.post('/api/connectors/cm360/cleanup', { temp_id: tempId });
        } catch (cleanupError) {
          console.warn('Failed to cleanup temp data:', cleanupError);
        }
        navigate('/connectors', {
          state: { message: response.data.message || 'Campaign Manager 360 connected', severity: 'success' }
        });
      } else {
        setError(response.data.message || 'Failed to connect the selected accounts');
      }
    } catch (err) {
      console.error('Error connecting Campaign Manager 360 accounts:', err);
      setError(err.response?.data?.message || 'Failed to connect the selected accounts');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading Campaign Manager 360 accounts...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/connectors')} sx={{ mr: 2 }}>
          Back
        </Button>
        <Box sx={{ mr: 2, display: 'flex', flexShrink: 0 }}>
          <Cm360Icon size={32} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Choose Campaign Manager 360 Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userEmail ? `Signed in as ${userEmail}. ` : ''}Each account you choose becomes its own connector.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select accounts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The connector syncs the account's advertisers, campaigns, placements, ads, creatives, Floodlight setup and daily reports, in the account's currency and report time zone.
          </Typography>

          {accounts.length === 0 ? (!error && (
            <Alert severity="warning">
              No Campaign Manager 360 user profile found for this Google login. Ask your Campaign Manager 360 admin to add this login as a user, then connect again.
            </Alert>
          )) : (
            <Grid container spacing={2}>
              {accounts.map((account) => {
                const isSelected = selected.includes(account.id);
                return (
                  <Grid item xs={12} md={6} key={account.id}>
                    <Card
                      variant="outlined"
                      onClick={() => toggle(account.id)}
                      sx={{
                        height: '100%',
                        cursor: connecting ? 'default' : 'pointer',
                        borderColor: isSelected ? GOOGLE_GREEN : undefined,
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
                      }}
                    >
                      <CardContent>
                        <FormControlLabel
                          onClick={(e) => e.stopPropagation()}
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggle(account.id)}
                              disabled={connecting}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', mr: 2 }}>
                                  <Cm360Icon size={22} />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {account.name || account.id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {account.id}{account.subaccount_name ? ` · ${account.subaccount_name}` : ''}
                                  </Typography>
                                  {account.alreadyConnected && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Chip
                                        label={account.connectedBrandName
                                          ? `Connected · ${account.connectedBrandName}`
                                          : 'Already connected'}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                              <Divider sx={{ my: 1 }} />
                              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                {account.currency && <Chip size="small" label={account.currency} />}
                                {account.time_zone && <Chip size="small" variant="outlined" label={account.time_zone} />}
                                {account.status && account.status !== 'ENABLED' && (
                                  <Chip size="small" color="warning" label={account.status} />
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      {selected.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Which advertisers are this brand's?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Only the advertisers you tick are read for this brand. Account-wide settings (users, roles, billing) are read only if you also confirm below that the whole account is your company's.
            </Typography>
            {loadingAdvertisers ? (
              <CircularProgress size={24} />
            ) : (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allPicked}
                      indeterminate={pickedAdvertisers.length > 0 && !allPicked}
                      onChange={() => setPickedAdvertisers(allPicked ? [] : advertisers.map(a => a.id))}
                      disabled={connecting || advertisers.length === 0}
                    />
                  }
                  label="Tick every advertiser"
                />
                {advertisers.map(a => (
                  <Box key={a.id} sx={{ pl: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={pickedAdvertisers.includes(a.id)}
                          onChange={() => toggleAdvertiser(a.id)}
                          disabled={connecting}
                        />
                      }
                      label={`${a.name || a.id} (${a.id})`}
                    />
                  </Box>
                ))}
                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={
                    <Checkbox
                      checked={wholeAccount}
                      onChange={() => setWholeAccount(v => !v)}
                      disabled={connecting || advertisers.length === 0}
                    />
                  }
                  label="The whole account is my company's: also read account-wide settings and advertisers added later (not available if another company has connected this account)"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {accounts.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/connectors')} disabled={connecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={selected.length === 0 || pickedAdvertisers.length === 0 || connecting}
            startIcon={connecting ? <CircularProgress size={20} /> : <CheckIcon />}
            sx={{ backgroundColor: GOOGLE_GREEN, '&:hover': { backgroundColor: '#1E8E3E' } }}
          >
            {connecting ? 'Connecting...' : `Connect ${pickedAdvertisers.length || ''} Advertiser${pickedAdvertisers.length === 1 ? '' : 's'}`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Cm360Selection;
